/**
 * Conditions Tracking Module
 * Handles paint condition, growth level, and anode condition buttons
 */

// Track selected button indices for each condition type
const selectedIndices = {
    paint: [],
    growth: []
};

// Paint button options (6 primary states)
const PAINT_BUTTONS = [
    { value: 'not_inspected', label: 'Not Inspected' },
    { value: 'excellent', label: 'Excellent' },
    { value: 'good', label: 'Good' },
    { value: 'fair', label: 'Fair' },
    { value: 'poor', label: 'Poor' },
    { value: 'missing', label: 'Missing' }
];

// Growth button options (6 primary states)
const GROWTH_BUTTONS = [
    { value: 'not_inspected', label: 'Not Inspected' },
    { value: 'minimal', label: 'Minimal' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'heavy', label: 'Heavy' },
    { value: 'severe', label: 'Severe' },
    { value: 'severe-maximum', label: 'Maximum' }
];

// Map button values to slider values for backward compatibility
const PAINT_VALUE_TO_SLIDER = {
    'not_inspected': 0,
    'excellent': 1,
    'excellent-good': 2,
    'good': 3,
    'good-fair': 4,
    'fair': 5,
    'fair-poor': 6,
    'poor': 7,
    'poor-missing': 8,
    'missing': 9
};

const GROWTH_VALUE_TO_SLIDER = {
    'not_inspected': 0,
    'minimal': 1,
    'minimal-moderate': 2,
    'moderate': 3,
    'moderate-heavy': 4,
    'heavy': 5,
    'heavy-severe': 6,
    'severe': 7,
    'severe-maximum': 8
};

// Display labels for compound values
const PAINT_DISPLAY_LABELS = {
    'not_inspected': 'Not Inspected',
    'excellent': 'Excellent',
    'excellent-good': 'Excellent-Good',
    'good': 'Good',
    'good-fair': 'Good-Fair',
    'fair': 'Fair',
    'fair-poor': 'Fair-Poor',
    'poor': 'Poor',
    'poor-missing': 'Poor-Missing',
    'missing': 'Missing'
};

const GROWTH_DISPLAY_LABELS = {
    'not_inspected': 'Not Inspected',
    'minimal': 'Minimal',
    'minimal-moderate': 'Minimal-Moderate',
    'moderate': 'Moderate',
    'moderate-heavy': 'Moderate-Heavy',
    'heavy': 'Heavy',
    'heavy-severe': 'Heavy-Severe',
    'severe': 'Severe',
    'severe-maximum': 'Severe (Maximum)'
};

/**
 * Handle button click for condition selection
 * @param {string} type - 'paint' or 'growth'
 * @param {HTMLElement} button - The clicked button
 */
export function handleConditionButtonClick(type, button) {
    const clickedIndex = parseInt(button.getAttribute('data-index'));
    const indices = selectedIndices[type];

    let newIndices;

    if (indices.length === 0) {
        // Nothing selected - select clicked
        newIndices = [clickedIndex];
    } else if (indices.length === 1) {
        const currentIndex = indices[0];

        if (currentIndex === clickedIndex) {
            // Same button - deselect
            newIndices = [];
        } else if (Math.abs(currentIndex - clickedIndex) === 1) {
            // Adjacent button - create range
            newIndices = [currentIndex, clickedIndex];
        } else {
            // Non-adjacent - replace selection
            newIndices = [clickedIndex];
        }
    } else {
        // Two selected - clear and select new
        newIndices = [clickedIndex];
    }

    selectedIndices[type] = newIndices;
    updateButtonDisplay(type);
    triggerPricingUpdate();
}

/**
 * Clear all selected buttons for a condition type
 * @param {string} type - 'paint' or 'growth'
 */
export function clearConditionButtons(type) {
    selectedIndices[type] = [];
    updateButtonDisplay(type);
    triggerPricingUpdate();
}

/**
 * Update button visual states and hidden input value
 * @param {string} type - 'paint' or 'growth'
 */
function updateButtonDisplay(type) {
    const containerId = type === 'paint' ? 'paintConditionButtons' : 'growthLevelButtons';
    const valueDisplayId = type === 'paint' ? 'paintConditionValue' : 'growthLevelValue';
    const hiddenInputId = type === 'paint' ? 'actualPaintCondition' : 'actualGrowthLevel';
    const buttons = type === 'paint' ? PAINT_BUTTONS : GROWTH_BUTTONS;
    const displayLabels = type === 'paint' ? PAINT_DISPLAY_LABELS : GROWTH_DISPLAY_LABELS;
    const valueToSlider = type === 'paint' ? PAINT_VALUE_TO_SLIDER : GROWTH_VALUE_TO_SLIDER;

    const container = document.getElementById(containerId);
    const valueDisplay = document.getElementById(valueDisplayId);
    const hiddenInput = document.getElementById(hiddenInputId);
    const indices = selectedIndices[type];

    if (!container) return;

    // Compute the data value
    const dataValue = indicesToDataValue(indices, buttons);
    const displayText = dataValue ? (displayLabels[dataValue] || dataValue) : 'Not Inspected';
    const sliderValue = dataValue ? (valueToSlider[dataValue] || 0) : 0;

    // Update container data-value
    container.setAttribute('data-value', dataValue || '');

    // Update display text
    if (valueDisplay) {
        valueDisplay.textContent = displayText;
    }

    // Update hidden input for backward compatibility
    if (hiddenInput) {
        hiddenInput.value = sliderValue;
        hiddenInput.setAttribute('data-value', dataValue || '');
        hiddenInput.setAttribute('data-user-set', 'true');
    }

    // Update button visual states
    const btnClass = type === 'paint' ? 'paint-btn' : 'growth-btn';
    const allButtons = container.querySelectorAll(`.${btnClass}`);
    const sortedIndices = [...indices].sort((a, b) => a - b);
    const minIndex = sortedIndices[0];
    const maxIndex = sortedIndices[sortedIndices.length - 1];

    allButtons.forEach((btn, index) => {
        const isSelected = indices.includes(index);
        const isInRange = indices.length === 2 && index > minIndex && index < maxIndex;

        btn.classList.remove('selected', 'in-range');
        if (isSelected) {
            btn.classList.add('selected');
        } else if (isInRange) {
            btn.classList.add('in-range');
        }
    });
}

/**
 * Convert selected indices to data value string
 * @param {number[]} indices - Selected button indices
 * @param {Object[]} buttons - Button option definitions
 * @returns {string|null} Data value
 */
function indicesToDataValue(indices, buttons) {
    if (indices.length === 0) return null;
    if (indices.length === 1) return buttons[indices[0]]?.value || null;

    // Two selected - create range value (lower index first = better condition first)
    const sorted = [...indices].sort((a, b) => a - b);
    const first = buttons[sorted[0]]?.value;
    const second = buttons[sorted[1]]?.value;

    if (!first || !second) return null;

    return `${first}-${second}`;
}

/**
 * Set button selection from a data value (e.g., 'fair-poor' or 'good')
 * Used for loading historical values
 * @param {string} type - 'paint' or 'growth'
 * @param {string} dataValue - The data value to set
 */
export function setConditionFromDataValue(type, dataValue) {
    const buttons = type === 'paint' ? PAINT_BUTTONS : GROWTH_BUTTONS;

    if (!dataValue || dataValue === 'not_inspected') {
        selectedIndices[type] = dataValue === 'not_inspected' ? [0] : [];
        updateButtonDisplay(type);
        return;
    }

    // Check for exact match first
    const exactIndex = buttons.findIndex(btn => btn.value === dataValue);
    if (exactIndex !== -1) {
        selectedIndices[type] = [exactIndex];
        updateButtonDisplay(type);
        return;
    }

    // Check for range value (e.g., 'fair-poor')
    const parts = dataValue.split('-');
    if (parts.length === 2) {
        const firstIndex = buttons.findIndex(btn => btn.value === parts[0]);
        const secondIndex = buttons.findIndex(btn => btn.value === parts[1]);
        if (firstIndex !== -1 && secondIndex !== -1) {
            selectedIndices[type] = [firstIndex, secondIndex];
            updateButtonDisplay(type);
            return;
        }
    }

    // Fallback - clear selection
    selectedIndices[type] = [];
    updateButtonDisplay(type);
}

/**
 * Get the current data value for a condition type
 * @param {string} type - 'paint' or 'growth'
 * @returns {string} Current data value
 */
export function getConditionDataValue(type) {
    const containerId = type === 'paint' ? 'paintConditionButtons' : 'growthLevelButtons';
    const container = document.getElementById(containerId);
    return container?.getAttribute('data-value') || 'not_inspected';
}

/**
 * Trigger pricing update when conditions change
 */
function triggerPricingUpdate() {
    if (window.updateChargeSummary && typeof window.updateChargeSummary === 'function') {
        console.log('Triggering charge summary update after condition change');
        window.updateChargeSummary();
    }

    if (window.updateWizardPricing && typeof window.updateWizardPricing === 'function') {
        window.updateWizardPricing();
    }
}

/**
 * Legacy function - Update slider display value (kept for backward compatibility)
 * Now just redirects to button-based logic if sliders don't exist
 */
export function updateSliderDisplay(sliderId, valueId) {
    // Check if this is a hidden input (new button-based system)
    const element = document.getElementById(sliderId);
    if (element && element.type === 'hidden') {
        // New button system - do nothing, buttons handle their own display
        return;
    }

    // Legacy slider code (kept for any remaining sliders like anodes)
    const slider = element;
    const valueDisplay = document.getElementById(valueId);

    if (!slider || !valueDisplay) return;

    const value = parseInt(slider.value);

    if (sliderId === 'actualPaintCondition') {
        const labels = ['Not Inspected', 'Excellent', 'Exc-Good', 'Good', 'Good-Fair', 'Fair', 'Fair-Poor', 'Poor', 'Poor-Missing', 'Missing'];
        const dataValues = ['not_inspected', 'excellent', 'excellent-good', 'good', 'good-fair', 'fair', 'fair-poor', 'poor', 'poor-missing', 'missing'];
        valueDisplay.textContent = labels[value] || 'Not Inspected';
        slider.setAttribute('data-value', dataValues[value]);
    } else if (sliderId === 'actualGrowthLevel') {
        const labels = [
            'Not Inspected', 'Minimal', 'Minimal-Moderate', 'Moderate', 'Moderate-Heavy',
            'Heavy', 'Heavy-Severe', 'Severe', 'Severe (Maximum)'
        ];
        const dataValues = [
            'not_inspected', 'minimal', 'minimal-moderate', 'moderate', 'moderate-heavy',
            'heavy', 'heavy-severe', 'severe', 'severe-maximum'
        ];
        valueDisplay.textContent = labels[value] || 'Not Inspected';
        slider.setAttribute('data-value', dataValues[value]);
    }

    triggerPricingUpdate();
}

// Expose to window for inline event handlers
window.updateSliderDisplay = updateSliderDisplay;
window.handleConditionButtonClick = handleConditionButtonClick;
window.clearConditionButtons = clearConditionButtons;
window.setConditionFromDataValue = setConditionFromDataValue;
window.getConditionDataValue = getConditionDataValue;
